
import { Comment } from '@/types/social';
import { ContentType } from '@/services/comments/types';
import { enrichCommentWithProfile } from '../enrichment/enrichCommentWithProfile';
import { fetchUpdatedCommentCount } from './updateCommentCount';

/**
 * Handle a new comment insertion event
 */
export async function handleNewCommentEvent(
  payload: any,
  contentId: string,
  contentType: ContentType,
  parentId: string | null,
  onNewComment?: (comment: Comment) => void,
  onCountChange?: (newCount: number) => void
): Promise<void> {
  try {
    console.log('New comment added:', payload);
    
    // Get the new comment from the payload
    const newComment = payload.new as Comment;
    
    // Check if this is a reply or top-level comment
    const isReplyToWatchedParent = parentId !== null && newComment.parent_id === parentId;
    const isTopLevelComment = parentId === null && newComment.parent_id === null;
    
    console.log(`Comment event: parentId=${parentId}, newComment.parent_id=${newComment.parent_id}`);
    console.log(`Is reply to watched parent: ${isReplyToWatchedParent}, Is top level: ${isTopLevelComment}`);
    
    if (isReplyToWatchedParent || isTopLevelComment) {
      // Always enrich the comment with profile data before notifying
      const enrichedComment = await enrichCommentWithProfile(newComment);
      console.log('Enriched comment with profile data:', enrichedComment);
      
      // Notify the parent component about the new comment
      if (onNewComment) {
        onNewComment(enrichedComment);
      }
      
      // Fetch updated count if needed
      if (onCountChange) {
        const updatedCount = await fetchUpdatedCommentCount(contentId, contentType, isReplyToWatchedParent ? parentId : null);
        onCountChange(updatedCount);
      }
    }
  } catch (error) {
    console.error('Error processing real-time comment insert:', error);
  }
}

/**
 * Handle a comment update event (e.g. when it's marked as deleted)
 */
export async function handleCommentUpdateEvent(
  payload: any,
  contentId: string,
  contentType: ContentType,
  onDeleteComment?: (commentId: string) => void,
  onCountChange?: (newCount: number) => void
): Promise<void> {
  try {
    console.log('Comment updated:', payload);
    const updatedComment = payload.new as Comment;
    
    // Check if comment was marked as deleted
    if (updatedComment.deleted) {
      if (onDeleteComment) {
        onDeleteComment(updatedComment.id);
      }
      
      // Update the count if comment was deleted
      if (onCountChange) {
        const updatedCount = await fetchUpdatedCommentCount(contentId, contentType, updatedComment.parent_id);
        onCountChange(updatedCount);
      }
    }
  } catch (error) {
    console.error('Error processing real-time comment update:', error);
  }
}

/**
 * Handle a comment deletion event
 */
export async function handleCommentDeleteEvent(
  payload: any,
  contentId: string,
  contentType: ContentType,
  onDeleteComment?: (commentId: string) => void,
  onCountChange?: (newCount: number) => void
): Promise<void> {
  try {
    console.log('Comment deleted:', payload);
    const deletedComment = payload.old as Comment;
    
    if (onDeleteComment) {
      onDeleteComment(deletedComment.id);
    }
    
    // Update count - passing the parent_id to ensure we update the correct count
    if (onCountChange) {
      const updatedCount = await fetchUpdatedCommentCount(contentId, contentType, deletedComment.parent_id);
      onCountChange(updatedCount);
    }
  } catch (error) {
    console.error('Error processing real-time comment delete:', error);
  }
}
